import type { Map as MaplibreMap } from 'maplibre-gl';
import { getUserData, storeUserData } from './CacheService';

const OVERLAY_STORAGE_KEY = 'efb-aviation-overlays';
const OVERLAY_METADATA_KEY = 'settings:aviation-overlays';
const LAYER_SUFFIXES = ['', '-fill', '-line', '-circle', '-label'];

export interface OverlaySetting {
  visible: boolean;
  opacity: number;
}

export type OverlayState = Record<string, OverlaySetting>;

function isOverlaySetting(value: unknown): value is OverlaySetting {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<OverlaySetting>;
  return typeof candidate.visible === 'boolean'
    && typeof candidate.opacity === 'number'
    && Number.isFinite(candidate.opacity);
}

export function mergeOverlayState(defaults: OverlayState, saved: unknown): OverlayState {
  if (!saved || typeof saved !== 'object') return defaults;
  const entries = Object.entries(defaults).map(([id, fallback]) => {
    const candidate = (saved as Record<string, unknown>)[id];
    if (!isOverlaySetting(candidate)) return [id, fallback];
    return [id, {
      visible: candidate.visible,
      opacity: Math.max(0, Math.min(1, candidate.opacity)),
    }];
  });
  return Object.fromEntries(entries);
}

export function loadOverlayStateSync(defaults: OverlayState): OverlayState {
  return mergeOverlayState(defaults, getSavedOverlayStateSync());
}

export function getSavedOverlayStateSync(): OverlayState {
  try {
    const raw = localStorage.getItem(OVERLAY_STORAGE_KEY);
    if (!raw) return {};
    const saved = JSON.parse(raw) as unknown;
    if (!saved || typeof saved !== 'object') return {};
    return Object.fromEntries(
      Object.entries(saved)
        .filter((entry): entry is [string, OverlaySetting] => isOverlaySetting(entry[1]))
        .map(([id, setting]) => [id, {
          visible: setting.visible,
          opacity: Math.max(0, Math.min(1, setting.opacity)),
        }]),
    );
  } catch {
    return {};
  }
}

export async function loadOverlayState(defaults: OverlayState): Promise<OverlayState> {
  const indexed = await getUserData<OverlayState>(OVERLAY_METADATA_KEY).catch(() => null);
  return indexed ? mergeOverlayState(defaults, indexed) : loadOverlayStateSync(defaults);
}

export async function saveOverlayState(state: OverlayState): Promise<void> {
  try { localStorage.setItem(OVERLAY_STORAGE_KEY, JSON.stringify(state)); } catch { /* unavailable */ }
  await storeUserData(OVERLAY_METADATA_KEY, state);
}

export function applyOverlayState(map: MaplibreMap, state: OverlayState): void {
  for (const [id, setting] of Object.entries(state)) {
    const visibility = setting.visible ? 'visible' : 'none';
    for (const suffix of LAYER_SUFFIXES) {
      const layerId = `${id}${suffix}`;
      if (!map.getLayer(layerId)) continue;
      map.setLayoutProperty(layerId, 'visibility', visibility);
      for (const property of ['raster-opacity', 'fill-opacity', 'line-opacity', 'circle-opacity']) {
        try { map.setPaintProperty(layerId, property, setting.opacity); } catch { /* property does not apply */ }
      }
    }
  }
}
